// src/features/records/hooks/useRecordCommentCount.js
import { useCallback, useEffect, useRef, useState } from 'react';
import { collection, getCountFromServer, query, where } from 'firebase/firestore';
import { db } from '../../../firebase';

const COMMENT_COUNT_CACHE_TTL_MS = 20_000;

const commentCountCache = new Map();
const commentCountRequests = new Map();
const commentCountSubscribers = new Map();
const commentCountEpochs = new Map();

const normaliseCount = (value) => {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  return Math.floor(value);
};

const getEpoch = (recordId) => commentCountEpochs.get(recordId) || 0;

const bumpEpoch = (recordId) => {
  const next = getEpoch(recordId) + 1;
  commentCountEpochs.set(recordId, next);
  return next;
};

const isEntryStale = (entry) => {
  if (!entry) return true;
  if (!Number.isFinite(entry.count)) return true;
  if (!Number.isFinite(entry.fetchedAt)) return true;
  return Date.now() - entry.fetchedAt > COMMENT_COUNT_CACHE_TTL_MS;
};

const notifySubscribers = (recordId) => {
  const listeners = commentCountSubscribers.get(recordId);
  if (!listeners || listeners.size === 0) return;
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      console.debug('Failed to notify comment count subscriber:', err);
    }
  });
};

const subscribe = (recordId, listener) => {
  if (!recordId || typeof listener !== 'function') {
    return () => {};
  }

  let listeners = commentCountSubscribers.get(recordId);
  if (!listeners) {
    listeners = new Set();
    commentCountSubscribers.set(recordId, listeners);
  }

  listeners.add(listener);

  return () => {
    const current = commentCountSubscribers.get(recordId);
    if (!current) return;
    current.delete(listener);
    if (current.size === 0) {
      commentCountSubscribers.delete(recordId);
    }
  };
};

// Count includes both root comments and replies (active only).
const fetchCommentCount = async (recordId, expectedEpoch) => {
  const commentsRef = collection(db, 'records', recordId, 'comments');
  const q = query(
    commentsRef,
    where('status', '==', 'active')
  );
  const snapshot = await getCountFromServer(q);
  const nextCount = normaliseCount(snapshot?.data()?.count);

  if (getEpoch(recordId) === expectedEpoch) {
    commentCountCache.set(recordId, {
      count: nextCount,
      fetchedAt: Date.now()
    });
  }

  return nextCount;
};

const getCommentCount = async (recordId, { bypassCache = false } = {}) => {
  if (!recordId) return 0;

  if (!bypassCache) {
    const cached = commentCountCache.get(recordId);
    if (cached && !isEntryStale(cached)) {
      return normaliseCount(cached.count);
    }
  }

  const existingRequest = commentCountRequests.get(recordId);
  if (existingRequest) {
    return existingRequest;
  }

  const expectedEpoch = getEpoch(recordId);
  const request = fetchCommentCount(recordId, expectedEpoch);
  commentCountRequests.set(recordId, request);

  try {
    return await request;
  } finally {
    if (commentCountRequests.get(recordId) === request) {
      commentCountRequests.delete(recordId);
    }
  }
};

export const invalidateRecordCommentCount = (recordId) => {
  if (!recordId) return;
  bumpEpoch(recordId);
  commentCountCache.delete(recordId);
  commentCountRequests.delete(recordId);
  notifySubscribers(recordId);
};

/**
 * Fetch aggregated active comment count for a record (roots + replies).
 *
 * @param {string} recordId
 * @param {{ enabled?: boolean }} [options]
 */
const useRecordCommentCount = (recordId, options = {}) => {
  const { enabled: enabledOption = true } = options;
  const enabled = Boolean(recordId && enabledOption);

  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSequenceRef = useRef(0);

  const fetchAndUpdate = useCallback(async ({ bypassCache = false } = {}) => {
    if (!enabled || !recordId) {
      return 0;
    }

    const currentSequence = ++fetchSequenceRef.current;

    setIsLoading(true);
    setError(null);

    try {
      const nextCount = await getCommentCount(recordId, { bypassCache });
      if (fetchSequenceRef.current !== currentSequence) {
        return nextCount;
      }
      setCount(nextCount);
      return nextCount;
    } catch (err) {
      if (fetchSequenceRef.current !== currentSequence) {
        return 0;
      }
      console.debug('Failed to fetch record comment count:', err);
      setError(err);
      return 0;
    } finally {
      if (fetchSequenceRef.current === currentSequence) {
        setIsLoading(false);
      }
    }
  }, [enabled, recordId]);

  useEffect(() => {
    if (!enabled || !recordId) {
      setCount(0);
      setIsLoading(false);
      setError(null);
      return;
    }

    const cached = commentCountCache.get(recordId);
    if (cached && Number.isFinite(cached.count)) {
      setCount(normaliseCount(cached.count));
    } else {
      setCount(0);
    }

    setError(null);

    if (cached && !isEntryStale(cached)) {
      setIsLoading(false);
      return;
    }

    fetchAndUpdate({ bypassCache: false });
  }, [recordId, enabled, fetchAndUpdate]);

  useEffect(() => {
    if (!recordId) return undefined;
    return subscribe(recordId, () => {
      fetchAndUpdate({ bypassCache: true });
    });
  }, [recordId, fetchAndUpdate]);

  const refresh = useCallback(() => {
    return fetchAndUpdate({ bypassCache: true });
  }, [fetchAndUpdate]);

  return {
    count,
    isLoading,
    error,
    refresh
  };
};

export default useRecordCommentCount;

export const __TEST__ = {
  COMMENT_COUNT_CACHE_TTL_MS,
  commentCountCache,
  commentCountRequests,
  commentCountSubscribers,
  commentCountEpochs,
  normaliseCount,
  isEntryStale
};
